import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import axios from "axios";
import { Plus, Trash2, Pencil, X, ExternalLink, Upload, FileText, Link2, RotateCcw, Copy } from "lucide-react";

const CATEGORIES = ["Article", "Reflection", "Video", "Book", "Podcast", "Guide", "Audio"];
const MAX_MB = 50;
const MAX_BYTES = MAX_MB * 1024 * 1024;

const emptyLink = {
  kind: "link",
  title: "",
  description: "",
  url: "",
  image_url: "",
  category: "Article",
  published: true,
  sort_order: 100,
};

const emptyFile = {
  kind: "file",
  title: "",
  description: "",
  category: "Guide",
  published: true,
  sort_order: 100,
};

function humanSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminResources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new-link" | "new-file" | resourceId
  const [form, setForm] = useState(emptyLink);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/resources");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNewLink = () => { setEditing("new-link"); setForm(emptyLink); setFile(null); setProgress(0); };
  const openNewFile = () => { setEditing("new-file"); setForm(emptyFile); setFile(null); setProgress(0); };

  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      kind: r.resource_kind || "link",
      title: r.title,
      description: r.description || "",
      url: r.url || "",
      image_url: r.image_url || "",
      category: r.category || "Article",
      published: r.published,
      sort_order: r.sort_order ?? 100,
      // read-only file info for display
      file_url: r.file_url,
      file_download_url: r.file_download_url,
      original_filename: r.original_filename,
      size_bytes: r.size_bytes,
      filename_slug: r.filename_slug,
      file_extension: r.file_extension,
    });
    setFile(null);
    setProgress(0);
  };

  const close = () => {
    setEditing(null);
    setForm(emptyLink);
    setFile(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onFilePick = (f) => {
    if (!f) { setFile(null); return; }
    if (f.size > MAX_BYTES) {
      toast.error(`File is too large (${(f.size / (1024*1024)).toFixed(1)} MB). Maximum is ${MAX_MB} MB.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFile(f);
  };

  const uploadWithProgress = async (url, formData) => {
    const token = getToken();
    return axios.post(`${API}${url}`, formData, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
      onUploadProgress: (e) => {
        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
      },
      timeout: 300000,
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new-link") {
        await api.post("/admin/resources", {
          title: form.title,
          description: form.description,
          url: form.url,
          image_url: form.image_url || null,
          category: form.category,
          published: form.published,
          sort_order: Number.isFinite(Number(form.sort_order)) ? Number(form.sort_order) : 100,
        });
        toast.success("Link resource created.");
      } else if (editing === "new-file") {
        if (!file) { toast.error("Please choose a file first."); setSaving(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("category", form.category);
        fd.append("published", form.published ? "true" : "false");
        fd.append("sort_order", String(Number.isFinite(Number(form.sort_order)) ? Number(form.sort_order) : 100));
        await uploadWithProgress("/admin/resources/upload", fd);
        toast.success("File uploaded and resource created.");
      } else {
        // Existing resource edit
        // 1) If a file resource has a new file selected, replace the file first
        if (form.kind === "file" && file) {
          const fd = new FormData();
          fd.append("file", file);
          await uploadWithProgress(`/admin/resources/${editing}/replace`, fd);
        }
        // 2) Then update the metadata (title/description/etc.)
        const payload = {
          title: form.title,
          description: form.description,
          url: form.kind === "link" ? form.url : "",
          image_url: form.kind === "link" ? (form.image_url || null) : null,
          category: form.category,
          published: form.published,
          sort_order: Number.isFinite(Number(form.sort_order)) ? Number(form.sort_order) : 100,
        };
        await api.put(`/admin/resources/${editing}`, payload);
        toast.success("Resource updated.");
      }
      close();
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed.";
      toast.error(String(msg));
    } finally {
      setSaving(false);
      setProgress(0);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await api.delete(`/admin/resources/${r.id}`);
      toast.success("Deleted.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  const copyUrl = async (url) => {
    if (!url) return;
    const full = url.startsWith("http") ? url : `${window.location.origin}${url}`;
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy failed — you can select and copy manually.");
    }
  };

  return (
    <div data-testid="admin-resources">
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-10">
        <div>
          <div className="eyebrow mb-2">Content</div>
          <h1 className="display text-[36px]">Resources</h1>
          <p className="text-[14px] text-brand-ink/60 mt-2">
            Add external links (articles, videos, podcasts) or upload files (PDFs, audio, guides) that live permanently on quantumresults.com.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={openNewLink}
            data-testid="new-link-btn"
            className="inline-flex items-center gap-2 rounded-full border border-brand-ink/30 bg-white px-5 py-2.5 text-brand-ink text-[13.5px] hover:border-brand-blue hover:text-brand-blue transition-colors"
          >
            <Link2 size={14} /> New external link
          </button>
          <button
            onClick={openNewFile}
            data-testid="new-file-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-brand-cream text-[13.5px] hover:bg-brand-blue transition-colors"
          >
            <Upload size={14} /> Upload a file
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-brand-ink/50 text-[14px]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-brand-rule bg-brand-cream/40 p-10 text-center">
          <p className="text-brand-ink/70">No resources yet. Add your first one.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-brand-rule/70 overflow-hidden bg-brand-cream">
          <table className="w-full text-[14px]">
            <thead className="text-left text-brand-ink/60 border-b border-brand-rule/70">
              <tr>
                <th className="px-5 py-3 font-medium">Title</th>
                <th className="px-5 py-3 font-medium">Kind</th>
                <th className="px-5 py-3 font-medium">Category</th>
                <th className="px-5 py-3 font-medium text-center">Order</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const isFile = r.resource_kind === "file";
                const linkPreview = isFile ? r.file_url : r.url;
                return (
                  <tr key={r.id} className="border-b border-brand-rule/40 last:border-0" data-testid={`row-${r.id}`}>
                    <td className="px-5 py-4">
                      <div className="font-medium text-brand-ink">{r.title}</div>
                      {linkPreview && (
                        <div className="flex items-center gap-3 mt-1">
                          <a href={linkPreview} target="_blank" rel="noopener noreferrer" className="text-[12px] text-brand-ink/50 hover:text-brand-blue inline-flex items-center gap-1 truncate max-w-md">
                            {linkPreview.slice(0, 70)}{linkPreview.length > 70 ? "…" : ""} <ExternalLink size={11} />
                          </a>
                          <button
                            onClick={() => copyUrl(linkPreview)}
                            data-testid={`copy-${r.id}`}
                            className="text-[11px] text-brand-ink/40 hover:text-brand-blue inline-flex items-center gap-1"
                            type="button"
                          >
                            <Copy size={11} /> copy
                          </button>
                        </div>
                      )}
                      {isFile && r.size_bytes ? (
                        <div className="text-[11px] text-brand-ink/40 mt-1">
                          {r.original_filename} · {humanSize(r.size_bytes)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-5 py-4 text-brand-ink/70">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${isFile ? "bg-brand-turquoise/10 text-brand-turquoise" : "bg-brand-blue/10 text-brand-blue"}`}>
                        {isFile ? <><FileText size={10} /> file</> : <><Link2 size={10} /> link</>}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-brand-ink/70">{r.category}</td>
                    <td className="px-5 py-4 text-brand-ink/60 text-center tabular-nums" data-testid={`order-${r.id}`}>{r.sort_order ?? 100}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] ${r.published ? "bg-brand-turquoise/15 text-brand-turquoise" : "bg-brand-rule/40 text-brand-ink/60"}`}>
                        {r.published ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} data-testid={`edit-${r.id}`} className="inline-flex items-center gap-1.5 text-brand-ink/70 hover:text-brand-blue mr-4">
                        <Pencil size={13} /> Edit
                      </button>
                      <button onClick={() => remove(r)} data-testid={`delete-${r.id}`} className="inline-flex items-center gap-1.5 text-brand-ink/70 hover:text-red-600">
                        <Trash2 size={13} /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-brand-ink/40 z-50 flex items-center justify-center p-4" onClick={close}>
          <div
            className="w-full max-w-xl rounded-lg bg-brand-cream border border-brand-rule/70 p-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="resource-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-[24px] text-brand-ink">
                {editing === "new-link" ? "New external link" :
                 editing === "new-file" ? "Upload a file" :
                 form.kind === "file" ? "Edit file resource" : "Edit external link"}
              </h2>
              <button onClick={close} className="text-brand-ink/60 hover:text-brand-ink"><X size={18} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <Field label="Title">
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="res-title" className={inputCls} />
              </Field>

              {form.kind === "link" ? (
                <>
                  <Field label="URL">
                    <input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="res-url" placeholder="https://…" className={inputCls} />
                  </Field>
                  <Field label="Image URL (optional)">
                    <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="res-image" placeholder="https://…" className={inputCls} />
                  </Field>
                </>
              ) : (
                <div>
                  <label className="block text-[11.5px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">
                    {editing === "new-file" ? "File" : "Replace file (optional)"}
                  </label>
                  {form.file_url && !file && (
                    <div className="mb-3 rounded-md border border-brand-rule/60 bg-white/60 p-3 text-[13px]">
                      <div className="flex items-center gap-2 text-brand-ink/80">
                        <FileText size={14} className="text-brand-turquoise" />
                        <span className="truncate">{form.original_filename}</span>
                        {form.size_bytes ? <span className="text-brand-ink/40">· {humanSize(form.size_bytes)}</span> : null}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px]">
                        <a href={form.file_url} target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline inline-flex items-center gap-1">
                          Open <ExternalLink size={10} />
                        </a>
                        <a href={form.file_download_url} className="text-brand-ink/70 hover:text-brand-blue inline-flex items-center gap-1">
                          Download
                        </a>
                        <button type="button" onClick={() => copyUrl(form.file_url)} className="text-brand-ink/60 hover:text-brand-blue inline-flex items-center gap-1">
                          <Copy size={11} /> Copy public URL
                        </button>
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={(e) => onFilePick(e.target.files?.[0])}
                    data-testid="res-file-input"
                    className="block w-full text-[13.5px] text-brand-ink/80 file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:text-[13px] file:bg-brand-ink file:text-brand-cream hover:file:bg-brand-blue file:cursor-pointer"
                  />
                  <div className="mt-2 flex items-center gap-3 text-[11.5px] text-brand-ink/50">
                    <span>Max {MAX_MB} MB · Filenames are normalised (lowercase, hyphenated) for the public URL.</span>
                    {file && <span className="text-brand-turquoise">Selected: {file.name} ({humanSize(file.size)})</span>}
                  </div>
                  {editing !== "new-file" && form.file_url && file && (
                    <p className="mt-2 text-[11.5px] text-brand-ink/50 inline-flex items-center gap-1">
                      <RotateCcw size={11} /> The public URL will be preserved unless the file extension changes.
                    </p>
                  )}
                  {progress > 0 && progress < 100 && (
                    <div className="mt-3 h-1.5 rounded-full bg-brand-rule/40 overflow-hidden">
                      <div className="h-full bg-brand-blue transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  )}
                </div>
              )}

              <Field label="Description">
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="res-description" className={inputCls} />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="res-category" className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <label className="flex items-center gap-2 py-3">
                    <input
                      type="checkbox"
                      checked={form.published}
                      onChange={(e) => setForm({ ...form, published: e.target.checked })}
                      data-testid="res-published"
                    />
                    <span className="text-[14px] text-brand-ink/80">Published</span>
                  </label>
                </Field>
              </div>

              <Field label="Display order (within this category — lower shows first; leave 100 for default)">
                <input
                  type="number"
                  min={0}
                  max={100000}
                  step={1}
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  data-testid="res-sort-order"
                  className={inputCls}
                />
              </Field>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={close} className="text-[14px] text-brand-ink/70 hover:text-brand-ink">Cancel</button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="res-save"
                  className="rounded-full bg-brand-ink px-5 py-2.5 text-brand-cream text-[13.5px] hover:bg-brand-blue transition-colors disabled:opacity-60"
                >
                  {saving ? (editing === "new-file" || (form.kind === "file" && file) ? `Uploading… ${progress || 0}%` : "Saving…") : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-brand-rule bg-white/80 px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-brand-blue";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11.5px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
